"use client"

import { useAtom } from "jotai"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "@/components/pop-ui/pop-toast"
import { Button } from "@/components/ui/button"
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
} from "@/components/ui/drawer"
import { privacyConsentAtom } from "@/lib/privacy/atom"
import { cn } from "@/lib/utils"
import { RuleModal } from "@/modules/cms/rule-modal"
import { consentUpdate } from "@/track/gtm"
import { useTracking } from "@/track/tracking-hooks"

interface PrivacyAgreementPopupProps {
	className?: string
	showDelay?: number // 显示延迟时间（毫秒），默认3000ms
}

export function PrivacyAgreementPopup({
	className,
	showDelay = 3000,
}: PrivacyAgreementPopupProps) {
	const [isOpen, setIsOpen] = useState(false)
	const { t } = useTranslation()
	const [privacyConsent, setPrivacyConsent] = useAtom(privacyConsentAtom)
	const { track, TrackingEventType } = useTracking()
	const impressionTrackedRef = useRef<boolean>(false)

	// 检查用户是否已经同意隐私协议
	useEffect(() => {
		const checkAgreementStatus = () => {
			if (!privacyConsent) {
				// 延迟显示弹窗
				const timer = setTimeout(() => {
					setIsOpen(true)
				}, showDelay)

				return () => clearTimeout(timer)
			}
		}

		checkAgreementStatus()
	}, [showDelay, privacyConsent])

	// 隐私横幅曝光埋点 - 弹窗显示时直接上报
	useEffect(() => {
		if (isOpen && !impressionTrackedRef.current) {
			track(
				"PopMartGlobalWebPrivacyPolicyPopupShow",
				{},
				TrackingEventType.VIEW,
			)
			impressionTrackedRef.current = true
		}
	}, [isOpen, track, TrackingEventType])

	// 处理接受协议
	const handleAccept = () => {
		setPrivacyConsent(true)
		setIsOpen(false)

		// 推送隐私同意更新埋点
		consentUpdate({
			gcs_analytics_storage: true,
			gcs_ad_storage: true,
			gcs_ad_user_data: true,
			gcs_ad_personalization: true,
		})
	}

	// 处理关闭弹窗
	const handleOverlayClick = () => {
		toast.error(t("main_policy_accept_msg", "请选中'接受'以继续。"))
	}

	if (!isOpen) {
		return null
	}

	return (
		<Drawer open={isOpen} onOpenChange={handleOverlayClick} dismissible={false}>
			<DrawerContent
				className={cn("z-99999 h-auto p-0 [&>div.bg-muted]:hidden", className)}
				onEscapeKeyDown={(e) => {
					e.preventDefault()
					handleOverlayClick()
				}}
				onInteractOutside={(e) => {
					e.preventDefault()
					handleOverlayClick()
				}}
			>
				<DrawerTitle className="sr-only">Privacy Agreement</DrawerTitle>
				<DrawerDescription className="sr-only">
					Privacy policy and terms agreement
				</DrawerDescription>
				<div className="container mx-auto flex flex-col items-center justify-center gap-4 px-4 py-4 md:flex-row">
					<div className="text-sm leading-relaxed text-gray-600">
						<p>
							{t("main_policy_agree_msg", "我同意")}{" "}
							<RuleModal
								slug="privacy-policy"
								title={t("main_policy_policy_text", "隐私政策")}
								showOverlay={false}
							>
								<span className="text-primary hover:text-primary/80 cursor-pointer underline underline-offset-4 transition-colors">
									{t("main_policy_policy_text", "隐私政策")}
								</span>
							</RuleModal>{" "}
							{t("user_register_and", "和")}{" "}
							<RuleModal
								slug="terms-and-conditions"
								title={t("main_policy_condition_text", "协议条款")}
								showOverlay={false}
							>
								<span className="text-primary hover:text-primary/80 cursor-pointer underline underline-offset-4 transition-colors">
									{t("main_policy_condition_text", "协议条款")}
								</span>
							</RuleModal>
						</p>
					</div>

					<Button
						onClick={handleAccept}
						variant={"default"}
						size="sm"
						className="w-[120px] px-6 py-2 text-sm font-medium"
					>
						{t("accept_text", "接受")}
					</Button>
				</div>
			</DrawerContent>
		</Drawer>
	)
}
